import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { fieldTypes } from '@app/consts';
import { MetadataFormService } from '@app/core/services/metadata/metadata-form.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { FormElementDialogV2Component } from '@app/modules/form-generator/edit-form-v3/components/form-element-dialog-v2/form-element-dialog-v2.component';
import { Form, FormElement, FormElementSubmit, FormElementVerbose } from '@app/modules/form-generator/edit-form-v3/models/form.model';
import { TableFieldVerbose } from '@app/modules/security-setup/models/table-field.model';
import { SecuritySetupService } from '@app/modules/security-setup/services/security-setup.service';
import { EmployeeSecurityService } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employee-security/services/employee-security.service';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { Localization } from '@app/shared/models/localization.model';
import { Culture } from '@app/shared/models/system-language/culture.model';
import moment from 'moment';
import { forkJoin, Observable, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { DemoFormDialogComponent } from '../demo-form-dialog/demo-form-dialog.component';
import { EmployeeService } from '@app/core/services/employee.service';
import { CurrentUserDetails } from '@app/shared/models/employee.model';

export interface FormElementFlags {
  required: boolean;
  hidden: boolean;
  readOnly: boolean;
  linked: boolean;
  helpText: string;
}

@Component({
  selector: 'app-form-elements-display',
  templateUrl: './form-elements-display.component.html',
  styleUrls: ['./form-elements-display.component.scss']
})
export class FormElementsDisplayComponent implements OnInit {
  public fieldTypes: typeof fieldTypes = fieldTypes;
  @Input() formId: string;
  @Output() formLoaded = new EventEmitter<Localization[]>();
  // What the canvas renders; only swapped once a refresh has finished so the list never shows half-built data
  displayElements: FormElement[] = [];
  // Snapshot of each element's status taken before app-form-field rewrites the element's properties
  elementFlags: { [id: string]: FormElementFlags } = {};
  hasLoaded: boolean = false;
  orderStatus: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
  private orderStatusTimeout: any;
  form: Form;
  loadingFormElements: boolean;
  formElements: FormElement[];
  formElementsToDelete: FormElement[] = [];
  tableFields
  loadingFormDetails: boolean;
  formElementsVerboseArray: FormElementVerbose[] = [];
  dbTableFields: TableFieldVerbose[];
  loadingDbTableFields: boolean = true;
  currentCulture: Culture;
  currentEmployeeDetails: CurrentUserDetails;

  constructor(
    private dialog: MatDialog,
    private metadataFormService: MetadataFormService,
    private snackbarService: SnackbarService,
    private securitySetupService: SecuritySetupService,
    private employeeSecurityService: EmployeeSecurityService,
    private employeeService: EmployeeService,
    private overlayService: OverlayService,
  ) { }

  ngOnInit(): void {
    this.currentEmployeeDetails = this.employeeService.getFetchedCurrentUserDetails()
    this.getCurrentCulture();
    this.getFormDetails();
    // this.getFormElements();
  }

  getCurrentCulture() {
    this.employeeSecurityService.currentCulture$()
    .subscribe(
      res => {
        this.currentCulture = res;
      }
    )
  }

  isFinishedLoading(): boolean {
    return this.hasLoaded;
  }

  getFormDetails() {
    this.loadingFormDetails = true;

    this.metadataFormService.getForm(this.formId)
      .pipe(
        finalize(() => {
          this.loadingFormDetails = false;
          this.getFormElements();
        })
      )
      .subscribe(
        res => {
          this.form = res;
          this.formLoaded.emit(res?.name ?? []);
        }
      );
  }

  getFormElements() {
    this.loadingFormElements = true;

    this.metadataFormService.getFormElements(this.formId, 1000, 0)
      .pipe(
        finalize(() => {
          this.loadingFormElements = false
        })
        )
      .subscribe(
          res => {
          this.formElements = res.data;
          this.getFormElementsVerbose();
          this.getDbTableField();
        }
      );
  }

  getDbTableField() {
    this.loadingDbTableFields = true;

    let observables: Observable<TableFieldVerbose>[] = [];

    this.formElements.forEach( formElement => {
      if(formElement?.tableField?.id !== undefined){
        observables.push(this.securitySetupService.getField(this.form.table?.id, formElement?.tableField?.id))
      }
    });

    forkJoin(observables)
    .pipe(
      finalize(()=>{
        this.loadingDbTableFields = false;
          this.sortIntoParentsAndChildren();
          this.buildElementFlags();
          this.displayElements = this.formElements;
          this.hasLoaded = true;
      })
    )
    .subscribe(
      res => {
        this.dbTableFields = res;
        this.addDBTableValues();
      }
    );
  }

  addDBTableValues() {
    this.dbTableFields.forEach(
      dbTableField => {
        let formElIndex = this.formElements.findIndex( el => el.tableField?.id === dbTableField?.id)

        if(formElIndex !== -1){
          this.formElements[formElIndex] = this.useDbTableFieldValues(this.formElements[formElIndex], dbTableField)
        }
      }
    )

  }

  useDbTableFieldValues(formElement: FormElement, dbTableField: any) {
    // set field name
    let currentCultureFieldName: Localization[] = dbTableField.name?.filter( n => n.culture === this.currentCulture?.id ) ?? [];
    formElement.text = (currentCultureFieldName[0] ?? dbTableField.name?.[0])?.text ?? formElement.text;

    // set field hidden value
    formElement.hidden = dbTableField.enabled ? 'false' : 'true';

    // set required value
    formElement.requiredField = dbTableField.requiredField ? 'true' : 'false';

    // set min length value
    // formElement.minLength = dbTableField.minimumValue;

    // set max length value
    // formElement.maxLength = dbTableField.maximumValue;

    // set the placeholder
    formElement.placeholder = dbTableField.defaultValue;

    return formElement
  }

  sortIntoParentsAndChildren() {
    let childrenToBeRemovedFromFormElementsArray: FormElement[] = [];

    this.formElements.forEach( formElement => {
      //If the form element is a container and it has no children array, add an empty one
      if(formElement.formElementType?.id === fieldTypes.CONTAINER && formElement.children === undefined) {
        formElement.children = [];
      }

      // extract the property values and add them to the form element
      formElement.properties?.forEach(prop => {
        formElement[prop.property] = prop.value;
      })

      if(formElement.parentFormElement !== null) {
        let parentId = formElement.parentFormElement.id;
        let parent = this.formElements.filter(formElement => formElement.id === parentId);

        //If the form element is a child
        if(parent.length === 1){
          //Get the form elements parent index
          let index = this.formElements.indexOf(parent[0]);

          if(index !== -1){
            // create a children array in the parent element
            if(this.formElements[index].children === undefined) {
              this.formElements[index].children = [];
            }

            childrenToBeRemovedFromFormElementsArray.push(formElement)

            // add current form element to the children array of its parent
            this.formElements[index].children.push(formElement);

            this.formElements[index].children.sort((firstItem, secondItem) => parseInt(firstItem.order) - parseInt(secondItem.order))
          }
        }
      }
    });

    // remove any child elements that have been copied to children arrays
    childrenToBeRemovedFromFormElementsArray.forEach( childFormElement => {
      let index = this.formElements.indexOf(childFormElement);

      if(index !== -1) {
        this.formElements.splice(index, 1);
      }
    });

    // sort the form elements by order
    this.formElements.sort((firstItem, secondItem) => parseInt(firstItem.order) - parseInt(secondItem.order))
  }

  openConfirmDeleteDialog(formElement: FormElement) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
        text: `Delete "${formElement.text}"? This can't be undone.`
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.deleteFormElement(formElement);
        }
      }
    );
  }

  openDemoFormDialog() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.autoFocus = false;
    dialogConfig.width = 'min(900px, 95vw)';
    dialogConfig.maxHeight = '90vh';

    dialogConfig.data = {
        formId: this.formId
    };

    const dialogRef = this.dialog.open(DemoFormDialogComponent, dialogConfig);
  }

  deleteFormElement(formElement: FormElement) {
    this.overlayService.show();

    this.metadataFormService.deleteFormElement(this.formId, formElement.id)
    .pipe(
      finalize( () => {
        this.overlayService.hide();
      })
    )
    .subscribe(
      (res) => {
        this.snackbarService.openSnackBar('Form Element Deleted Successfully', 'clear', 'success');
        this.getFormElements();
      }
    );
  }

  openFormElementDialog(formTableId: string, formElement?: FormElement, parent?: FormElement, order?: number) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    dialogConfig.width = 'min(760px, 95vw)';
    dialogConfig.maxWidth = '95vw';
    dialogConfig.maxHeight = '90vh';

    dialogConfig.data = {
      formId: this.formId,
      formElementId: formElement ? formElement.id : null,
      parentId: parent ? parent.id : null,
      formTableId: formTableId ? formTableId : null,
      order: order ?? null
    };

    const dialogRef = this.dialog.open(FormElementDialogV2Component, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if(data === true) {
          this.getFormElements();
        }
      }
    );
  }

  addElement() {
    this.openFormElementDialog(this.form?.table?.id, null, null, this.displayElements.length);
  }

  addChildElement(container: FormElement) {
    this.openFormElementDialog(this.form?.table?.id, null, container, container.children?.length ?? 0);
  }

  editElement(formElement: FormElement) {
    this.openFormElementDialog(this.form?.table?.id, formElement);
  }

  // reorder the list after drag and drop and save the new order straight away
  drop(event: CdkDragDrop<FormElement[]>, list: FormElement[]) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(list, event.previousIndex, event.currentIndex);
    this.saveOrder(list);
  }

  typeIcon(formElement: FormElement): string {
    const id = formElement.formElementType?.id ?? '';

    switch (id) {
      case fieldTypes.CONTAINER: return 'view_agenda';
      case fieldTypes.TOGGLE: return 'toggle_on';
      case fieldTypes.DATE_INPUT: return 'calendar_today';
      case fieldTypes.DATE_TIME: return 'event';
      case fieldTypes.TIME_INPUT: return 'schedule';
      case fieldTypes.NUMBER_INPUT:
      case fieldTypes.DECIMAL_INPUT: return 'pin';
      case fieldTypes.TEXT_AREA:
      case fieldTypes.TEXT_AREA_LOCALIZATION: return 'subject';
      case fieldTypes.TEXT_LOCALIZATION: return 'translate';
      case fieldTypes.HELP_TEXT: return 'help_outline';
      case fieldTypes.FILE_UPLOAD: return 'attach_file';
      case fieldTypes.IMAGE_UPLOAD: return 'image';
      case fieldTypes.EMPLOYEE_SELECT: return 'person';
      case fieldTypes.MULTIPLE_EMPLOYEE_SELECT: return 'groups';
      case fieldTypes.DROPDOWN: return 'list';
    }

    return /dropdown/i.test(id) ? 'list' : 'short_text';
  }

  // get the detailed versions of all the form elements
  getFormElementsVerbose() {
    this.formElementsVerboseArray = [];

    const requests = this.formElements.map(formElement => this.metadataFormService.getFormElement(this.formId, formElement.id));

    (requests.length ? forkJoin(requests) : of([] as FormElementVerbose[]))
      .subscribe(res => this.formElementsVerboseArray = res);
  }

  private buildElementFlags() {
    this.elementFlags = {};

    const addFlags = (formElement: FormElement) => {
      this.elementFlags[formElement.id] = {
        required: String(formElement.requiredField) === 'true',
        hidden: String(formElement.hidden) === 'true',
        readOnly: String(formElement.disabled) === 'true',
        linked: !!formElement.tableField?.id,
        helpText: formElement.tooltipText || null,
      };

      formElement.children?.forEach(addFlags);
    };

    this.formElements.forEach(addFlags);
  }

  // Saves the order of one list (the top level or a section's children) after it has been reordered
  saveOrder(list: FormElement[]) {
    const updateRequests: Observable<any>[] = [];

    for (const [index, element] of list.entries()) {
      const formElementVerbose = this.formElementsVerboseArray.find(verbose => verbose.id === element.id);

      if (!formElementVerbose) {
        this.orderStatus = 'error';
        this.snackbarService.openSnackBar('The form is still loading, please try again in a moment', 'clear', 'warn');
        this.getFormElements();
        return;
      }

      this.addNewOrderValue(formElementVerbose, index);
      element.order = index.toString();
      updateRequests.push(this.metadataFormService.updateFormElement(this.formId, formElementVerbose.id, this.createFormElementSubmit(formElementVerbose)));
    }

    clearTimeout(this.orderStatusTimeout);
    this.orderStatus = 'saving';

    forkJoin(updateRequests)
    .subscribe({
      next: () => {
        this.orderStatus = 'saved';
        this.orderStatusTimeout = setTimeout(() => this.orderStatus = 'idle', 2500);
      },
      error: () => {
        this.orderStatus = 'error';
        this.getFormElements();
      }
    });
  }

  addNewOrderValue(formElementVerbose: FormElementVerbose, index: number): FormElementVerbose {
    //remove any order property value it has
    formElementVerbose.properties = formElementVerbose.properties.filter(property => property.property !== 'order');

    //add a new order property value
    formElementVerbose.properties.push(
      {
        property: 'order',
        value: index.toString()
      }
    )

    return formElementVerbose;
  }

  createFormElementSubmit(formElementVerbose: FormElementVerbose): FormElementSubmit {
    return {
      id: formElementVerbose.id,
      asOf: moment().format(),
      changeReason: null,
      changeReasonComments: null,
      name: formElementVerbose.text,
      properties: formElementVerbose.properties,
      formElementType: formElementVerbose.formElementType?.id,
      parentFormElement: formElementVerbose.parentFormElement?.id,
      tableField: formElementVerbose.tableField?.id
    }
  }
}
